/**
 * Poses the three.js camera. Runs at render rate, never at simulation rate.
 *
 * Camera sway is *cosmetic only*: it must never feed back into movement, or the
 * "camera sway intensity" setting would become a competitive advantage.
 */

import type { PerspectiveCamera } from 'three';
import { FINISH, MOMENTUM, PLAYER, WAKE_UP } from '../config/gameplay';
import { clamp, lerp, smoothStep, type Vec3 } from '../utils/math';
import type { IntoxicationSample } from './IntoxicationSystem';

export interface CameraPose {
  /** Interpolated player feet position. */
  position: Vec3;
  yaw: number;
  pitch: number;
  /** Horizontal speed, scales the head bob. */
  speed: number;
  intox: IntoxicationSample;
  /** User setting, 0..2. */
  swayIntensity: number;
  /** 0 while lying in bed, 1 once fully upright. */
  wakeUpProgress: number;
  /** 0 while running, 1 once seated at the finish. */
  sitProgress: number;
  seat: { eye: Vec3; yaw: number; pitch: number };
  fov: number;
}

export class CameraController {
  constructor(private readonly camera: PerspectiveCamera) {}

  update(pose: CameraPose): void {
    const wake = smoothStep(pose.wakeUpProgress);
    const eyeHeight = lerp(WAKE_UP.startEyeHeight, PLAYER.eyeHeight, wake);
    const wakeRoll = lerp(WAKE_UP.startRoll, 0, wake);

    // Head bob scales with how fast we are actually moving, so standing still
    // is calm and sprinting is a mess. The ceiling is the vauhti-meter's top
    // speed rather than the base speed: clamping at 1.2 would flatten the whole
    // upper half of the meter into one look, and how fast you are going is
    // exactly what the camera should be telling you.
    const speedFactor = clamp(pose.speed / PLAYER.maxSpeed, 0, MOMENTUM.max);
    const sway = pose.swayIntensity;

    let x = pose.position.x;
    let y = pose.position.y + eyeHeight + pose.intox.bobVertical * speedFactor * sway;
    let z = pose.position.z;

    // Lateral bob rides along the camera's right vector.
    const bob = pose.intox.bobLateral * speedFactor * sway;
    x += Math.cos(pose.yaw) * bob;
    z += -Math.sin(pose.yaw) * bob;

    let yaw = pose.yaw + pose.intox.cameraYaw * sway;
    let pitch = pose.pitch + pose.intox.cameraPitch * sway + WAKE_UP.startPitch * (1 - wake);
    let roll = pose.intox.cameraRoll * sway + wakeRoll;

    if (pose.sitProgress > 0) {
      const t = smoothStep(pose.sitProgress);
      x = lerp(x, pose.seat.eye.x, t);
      y = lerp(y, pose.seat.eye.y, t);
      z = lerp(z, pose.seat.eye.z, t);
      yaw = lerpAngle(yaw, pose.seat.yaw, t);
      pitch = lerp(pitch, pose.seat.pitch, t);
      roll = lerp(roll, 0, t);
    }

    this.camera.position.set(x, y, z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.set(pitch, yaw, roll);

    if (Math.abs(this.camera.fov - pose.fov) > 1e-3) {
      this.camera.fov = pose.fov;
      this.camera.updateProjectionMatrix();
    }
  }
}

/** How far through the sit-down transition we are, given render time. */
export function sitProgress(secondsSinceFinish: number): number {
  return clamp(secondsSinceFinish / FINISH.sitDuration, 0, 1);
}

function lerpAngle(a: number, b: number, t: number): number {
  let delta = (b - a) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return a + delta * t;
}
